import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@ndd/ui";
import React, {
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import type {
  AppointmentReEnhanceInput,
  AppointmentReEnhanceResult,
} from "@/lib/appointment-documentation-state";

type ReEnhanceRefusal = Exclude<AppointmentReEnhanceResult, { status: "started" }>;

/** NO-785: what the dialog says when a confirm did not start a run. */
const RE_ENHANCE_ERROR_COPY: Record<ReEnhanceRefusal["status"], string> = {
  draft_changed:
    "Your draft changed since you opened it. Review the latest version and try again.",
  draft_approved:
    "This draft was already approved and sent to Healthie, so it can't be re-enhanced.",
  failed: "Re-enhance didn't start. Try again in a moment.",
};

export function reEnhanceErrorMessage(result: ReEnhanceRefusal): string {
  return RE_ENHANCE_ERROR_COPY[result.status];
}

export interface ReEnhanceConfirmOutcome {
  close: boolean;
  error: string | null;
  /** The head moved or was approved: only reopening captures a fresh one. */
  blocked: boolean;
}

/**
 * Sends exactly the request captured when the dialog opened: the head this
 * tab showed and its claim key. A head that moved since then answers
 * draft_changed, so a stale confirm never replaces a newer draft.
 */
export async function confirmReEnhance(
  request: AppointmentReEnhanceInput,
  reEnhance: (input: AppointmentReEnhanceInput) => Promise<AppointmentReEnhanceResult>
): Promise<ReEnhanceConfirmOutcome> {
  const result = await reEnhance(request);
  if (result.status === "started") return { close: true, error: null, blocked: false };

  return {
    close: false,
    error: reEnhanceErrorMessage(result),
    blocked: result.status !== "failed",
  };
}

/**
 * The Re-enhance confirmation. It replaces the current draft, edits included,
 * so it always asks first; while the request is pending both buttons wait.
 */
export function ReEnhanceDialog({
  open,
  pending,
  error,
  blocked,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  error: string | null;
  blocked: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}): ReactElement {
  const confirm = (event: MouseEvent<HTMLButtonElement>) => {
    // The dialog stays open until the server answers.
    event.preventDefault();
    onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Re-enhance this draft?</AlertDialogTitle>
          <AlertDialogDescription>
            This replaces your current draft, including any edits you made to it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={pending || blocked} onClick={confirm}>
            {pending ? "Starting…" : "Re-enhance"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** NO-785: the dialog's whole state; `request` is set exactly while it is open. */
export interface ReEnhanceFlow {
  request: AppointmentReEnhanceInput | null;
  pending: boolean;
  error: string | null;
  blocked: boolean;
}

export const CLOSED_RE_ENHANCE_FLOW: ReEnhanceFlow = {
  request: null,
  pending: false,
  error: null,
  blocked: false,
};

/** Opening captures the head this tab shows and a fresh claim key. */
export function openReEnhanceFlow(
  draftRevisionId: string,
  claimKey: string
): ReEnhanceFlow {
  return { ...CLOSED_RE_ENHANCE_FLOW, request: { claimKey, draftRevisionId } };
}

/** Cancel or dismiss: closes without sending anything, except mid-request. */
export function closeReEnhanceFlow(flow: ReEnhanceFlow): ReEnhanceFlow {
  return flow.pending ? flow : CLOSED_RE_ENHANCE_FLOW;
}

/** Confirm: sends the captured request once; a started run closes the dialog. */
export async function confirmReEnhanceFlow(
  flow: ReEnhanceFlow,
  reEnhance: (input: AppointmentReEnhanceInput) => Promise<AppointmentReEnhanceResult>
): Promise<ReEnhanceFlow> {
  if (!flow.request || flow.pending) return flow;

  const outcome = await confirmReEnhance(flow.request, reEnhance);
  if (outcome.close) return CLOSED_RE_ENHANCE_FLOW;

  return { ...flow, pending: false, error: outcome.error, blocked: outcome.blocked };
}

/**
 * NO-785: the Re-enhance action, offered only while the workspace says so
 * (both `soap-enhancement` and `soap-re-enhance` on, and the Draft never
 * approved) and a head is on screen. Off, the footer gets no handler and no
 * dialog mounts. Opening captures the head the tab shows and a fresh claim
 * key; confirming sends both.
 */
export function useReEnhanceAction(
  reEnhance: (input: AppointmentReEnhanceInput) => Promise<AppointmentReEnhanceResult>,
  enabled: boolean,
  draftRevisionId: string | null
): { dialog: ReactNode; footerProps: { onReEnhance?: () => void } } {
  const [flow, setFlow] = useState<ReEnhanceFlow>(CLOSED_RE_ENHANCE_FLOW);

  if (!enabled || draftRevisionId === null) return { dialog: null, footerProps: {} };

  const confirm = async () => {
    if (!flow.request || flow.pending) return;

    setFlow({ ...flow, pending: true });
    setFlow(await confirmReEnhanceFlow(flow, reEnhance));
  };

  return {
    dialog: (
      <ReEnhanceDialog
        open={flow.request !== null}
        pending={flow.pending}
        error={flow.error}
        blocked={flow.blocked}
        onOpenChange={(next) => !next && setFlow(closeReEnhanceFlow)}
        onConfirm={() => void confirm()}
      />
    ),
    footerProps: {
      onReEnhance: () =>
        setFlow(openReEnhanceFlow(draftRevisionId, crypto.randomUUID())),
    },
  };
}
